import React, { useEffect } from 'react';
import { useModal } from '../hooks/useModal';
import Modal from './Modal';

const VERSION_STORAGE_KEY = 'app_version';
const VERSION_CHECK_INTERVAL = 5 * 60 * 1000; // Check every 5 minutes

/**
 * Component to check for app updates and show update notification
 * ONLY to users who have installed the app (standalone mode)
 * Browser users will NOT see the update popup
 */
export default function AppUpdateChecker() {
  const { modal, showModal, hideModal } = useModal();

  useEffect(() => {
    // Check if app is running in standalone mode (installed on home screen)
    const checkStandalone = () => {
      // Method 1: Check window.matchMedia for standalone display mode
      if (window.matchMedia && window.matchMedia('(display-mode: standalone)').matches) {
        console.log('[AppUpdateChecker] Standalone mode detected: display-mode: standalone');
        return true;
      }
      
      // Method 2: Check for iOS standalone mode
      if (window.navigator.standalone === true) {
        console.log('[AppUpdateChecker] Standalone mode detected: iOS standalone');
        return true;
      }
      
      // Method 3: Check if launched from home screen (Android - fullscreen mode)
      if (window.matchMedia && window.matchMedia('(display-mode: fullscreen)').matches) {
        console.log('[AppUpdateChecker] Standalone mode detected: display-mode: fullscreen');
        return true;
      }
      
      // Method 4: Check if running in PWA mode (window.matchMedia might not work in all cases)
      if (window.matchMedia && (window.matchMedia('(display-mode: standalone)').matches || 
          window.matchMedia('(display-mode: fullscreen)').matches)) {
        return true;
      }
      
      console.log('[AppUpdateChecker] Not in standalone mode - browser user');
      return false;
    };

    const standalone = checkStandalone();

    // Only check for updates if app is installed (standalone mode)
    if (!standalone) {
      console.log('[AppUpdateChecker] Skipping update check - not in standalone mode');
      return; // Browser users - no update check
    }

    // Fetch current version from manifest.json
    const fetchCurrentVersion = async () => {
      try {
        const response = await fetch('/manifest.json');
        if (response.ok) {
          const manifest = await response.json();
          const version = manifest.version || '1.0.0';
          console.log('[AppUpdateChecker] Current version from manifest:', version);
          return version;
        } else {
          // Fallback to a default version if manifest fetch fails
          const fallbackVersion = '1.0.0';
          console.warn('[AppUpdateChecker] Failed to fetch manifest, using fallback version:', fallbackVersion);
          return fallbackVersion;
        }
      } catch (error) {
        console.error('[AppUpdateChecker] Error fetching manifest:', error);
        const fallbackVersion = '1.0.0';
        return fallbackVersion;
      }
    };

    // Check for updates
    const checkForUpdates = async () => {
      const manifestVersion = await fetchCurrentVersion();
      if (!manifestVersion) return;

      const storedVersion = localStorage.getItem(VERSION_STORAGE_KEY);
      const manualRefreshFlag = localStorage.getItem('manual_refresh_flag');
      const manualRefreshVersion = localStorage.getItem('manual_refresh_version');
      
      console.log('[AppUpdateChecker] Stored version:', storedVersion, 'Current version:', manifestVersion);
      console.log('[AppUpdateChecker] Manual refresh flag:', manualRefreshFlag, 'Manual refresh version:', manualRefreshVersion);
      
      // Check if we just did a manual refresh
      if (manualRefreshFlag === 'true') {
        console.log('[AppUpdateChecker] Manual refresh detected');
        
        // Clear the manual refresh flag
        localStorage.removeItem('manual_refresh_flag');
        
        // Check if version changed during refresh (new deployment)
        if (manualRefreshVersion && manualRefreshVersion !== manifestVersion) {
          // Version changed! New version was deployed during/after refresh
          console.log('[AppUpdateChecker] Version changed during refresh! Old:', manualRefreshVersion, 'New:', manifestVersion);
          // Update stored version
          localStorage.setItem(VERSION_STORAGE_KEY, manifestVersion);
          localStorage.removeItem('manual_refresh_version');
          // Show update notification
          setTimeout(() => {
            showModal({
              title: 'App Update Available',
              message: 'A new version of the app is available! Please refresh the page to get the latest updates.',
              type: 'info',
              onConfirm: () => {
                // Update stored version again (in case user clicks)
                localStorage.setItem(VERSION_STORAGE_KEY, manifestVersion);
                console.log('[AppUpdateChecker] User confirmed update, reloading...');
                // Reload the page to get the new version
                window.location.reload();
              },
              confirmText: 'Refresh Now',
              cancelText: 'Later',
              showCancel: true
            });
          }, 2000); // Show after 2 seconds
        } else {
          // Same version after refresh - don't show popup, just update stored version
          console.log('[AppUpdateChecker] Same version after manual refresh - not showing popup');
          localStorage.setItem(VERSION_STORAGE_KEY, manifestVersion);
          localStorage.removeItem('manual_refresh_version');
        }
        return; // Exit early after handling manual refresh
      }
      
      // Normal update check (no manual refresh)
      if (!storedVersion) {
        // First time - just store the version
        console.log('[AppUpdateChecker] First time - storing version');
        localStorage.setItem(VERSION_STORAGE_KEY, manifestVersion);
        return;
      }
      
      if (storedVersion !== manifestVersion) {
        // New version detected
        console.log('[AppUpdateChecker] New version detected! Showing update notification');
        // Show update notification after a short delay
        setTimeout(() => {
          showModal({
            title: 'App Update Available',
            message: 'A new version of the app is available! Please refresh the page to get the latest updates.',
            type: 'info',
            onConfirm: () => {
              // Update stored version
              localStorage.setItem(VERSION_STORAGE_KEY, manifestVersion);
              console.log('[AppUpdateChecker] User confirmed update, reloading...');
              // Reload the page to get the new version
              window.location.reload();
            },
            confirmText: 'Refresh Now',
            cancelText: 'Later',
            showCancel: true
          });
        }, 2000); // Show after 2 seconds
      } else {
        console.log('[AppUpdateChecker] Same version - no update needed');
        // Same version, ensure stored version is current
        localStorage.setItem(VERSION_STORAGE_KEY, manifestVersion);
      }
    };

    // Initial check
    checkForUpdates();

    // Set up periodic checks (every 5 minutes)
    const intervalId = setInterval(() => {
      console.log('[AppUpdateChecker] Periodic version check');
      checkForUpdates();
    }, VERSION_CHECK_INTERVAL);

    return () => {
      clearInterval(intervalId);
    };
  }, [showModal]);

  // Render modal if needed
  return <Modal {...modal} onClose={hideModal} />;
}

